import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import type { HospitalDto, HospitalInput } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { downloadCsv, parseCsv } from "../lib/csv";
import { btnPrimary, btnSecondary, ErrorBox, Field, Forbidden, inputCls, Modal, Spinner } from "../components/ui";

export function HospitalsPage() {
  const { me, isLoading } = useMe();
  const api = useApi();
  const qc = useQueryClient();
  const hospitals = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["hospitals"] });

  const create = useMutation({ mutationFn: (h: HospitalInput) => api.post<HospitalDto>("/api/v1/hospitals", h), onSuccess: invalidate });
  const update = useMutation({ mutationFn: ({ id, ...h }: HospitalInput & { id: string }) => api.patch<HospitalDto>(`/api/v1/hospitals/${id}`, h), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.del<void>(`/api/v1/hospitals/${id}`), onSuccess: invalidate });
  const bulk = useMutation({ mutationFn: (rows: HospitalInput[]) => api.post<{ created: number; skipped: number }>("/api/v1/hospitals/bulk", { hospitals: rows }), onSuccess: invalidate });

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<HospitalDto | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [city, setCity] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;

  function openCreate() {
    setEditing(null);
    setName("");
    setCode("");
    setCity("");
    setErr(null);
    setShowForm(true);
  }
  function openEdit(h: HospitalDto) {
    setEditing(h);
    setName(h.name);
    setCode(h.code ?? "");
    setCity(h.city ?? "");
    setErr(null);
    setShowForm(true);
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      const input = { name, code: code || undefined, city: city || undefined };
      if (editing) await update.mutateAsync({ id: editing.id, ...input });
      else await create.mutateAsync(input);
      setShowForm(false);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  const saving = create.isPending || update.isPending;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Hospitals / Medical Centers</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowImport(true)} className={btnSecondary}>
            <Upload className="w-4 h-4" /> Bulk import
          </button>
          <button type="button" onClick={openCreate} className={btnPrimary}>
            <Plus className="w-4 h-4" /> Add Hospital
          </button>
        </div>
      </div>

      {hospitals.isPending ? (
        <Spinner />
      ) : hospitals.isError ? (
        <ErrorBox>{hospitals.error.message}</ErrorBox>
      ) : hospitals.data.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600">No hospitals yet. Add one to get started.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {hospitals.data.map((h) => (
                  <tr key={h.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{h.name}</td>
                    <td className="px-4 py-3 text-slate-700">{h.code || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{h.city || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(h)} className="text-slate-600 hover:text-slate-900 p-1.5 rounded" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (confirm(`Delete "${h.name}"? This will detach it from any assigned supervisors.`)) {
                            try {
                              await remove.mutateAsync(h.id);
                            } catch (e) {
                              alert((e as Error).message);
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-700 p-1.5 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <Modal title={editing ? "Edit hospital" : "Add hospital"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <Field label="Name *">
              <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Code">
              <input value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} />
            </Field>
            <Field label="City">
              <input value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} />
            </Field>
            {err && <ErrorBox>{err}</ErrorBox>}
            <div className="flex gap-2 pt-2">
              <button type="submit" disabled={saving} className={`${btnPrimary} flex-1 justify-center`}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? "Save" : "Create"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showImport && <BulkImportModal onClose={() => setShowImport(false)} runImport={(rows) => bulk.mutateAsync(rows)} />}
    </div>
  );
}

function BulkImportModal({ onClose, runImport }: { onClose: () => void; runImport: (rows: HospitalInput[]) => Promise<{ created: number; skipped: number }> }) {
  const [parsed, setParsed] = useState<HospitalInput[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  function downloadTemplate() {
    downloadCsv("hospitals-template.csv", "Name,Code,City\nDr Sulaiman Al Habib - Olaya,OLY,Riyadh\nDr Sulaiman Al Habib - Qassim,QSM,Buraydah\n");
  }
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    setResult(null);
    setParsed(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const rows = parseCsv(await file.text()).filter((r) => r.some((c) => c.trim() !== ""));
      if (rows.length < 1) throw new Error("The file looks empty.");
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const nameIdx = header.indexOf("name");
      if (nameIdx === -1) throw new Error('Missing a "Name" column header. Use the template.');
      const codeIdx = header.indexOf("code");
      const cityIdx = header.indexOf("city");
      const list = rows
        .slice(1)
        .map((r) => ({ name: (r[nameIdx] ?? "").trim(), code: codeIdx >= 0 ? (r[codeIdx] ?? "").trim() || undefined : undefined, city: cityIdx >= 0 ? (r[cityIdx] ?? "").trim() || undefined : undefined }))
        .filter((h) => h.name !== "");
      if (list.length === 0) throw new Error("No rows with a hospital name were found.");
      setParsed(list);
    } catch (err) {
      setError((err as Error).message ?? "Could not read the file.");
    }
  }
  async function doImport() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await runImport(parsed));
    } catch (err) {
      setError((err as Error).message ?? "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={<><Upload className="w-5 h-5" /> Bulk import hospitals</>} onClose={onClose}>
      {result ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">
            Added <span className="font-semibold">{result.created}</span> hospital{result.created === 1 ? "" : "s"}.
            {result.skipped > 0 && <> Skipped <span className="font-semibold">{result.skipped}</span> (blank or duplicate name).</>}
          </div>
          <button type="button" onClick={onClose} className="w-full rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2">Done</button>
        </div>
      ) : (
        <div className="space-y-4">
          <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
            <li>Download the template.</li>
            <li>Fill one hospital per row. <strong>Name</strong> is required; Code and City are optional.</li>
            <li>Save as CSV and upload it below.</li>
          </ol>
          <button type="button" onClick={downloadTemplate} className="w-full rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-2 inline-flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download template (CSV)
          </button>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Upload filled CSV</span>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold hover:file:bg-slate-200" />
          </label>
          {parsed && !error && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3">
              <span className="font-semibold">{parsed.length}</span> hospital{parsed.length === 1 ? "" : "s"} found in <span className="font-mono">{fileName}</span>, ready to import.
            </div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={doImport} disabled={busy || !parsed} className={`${btnPrimary} flex-1 justify-center`}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Import{parsed ? ` ${parsed.length}` : ""}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
